import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import styles from "../components/FolderView.module.css";
import { ListPending } from "../components/FolderView.tsx";
import { GooglyEyes } from "../components/GooglyEyes.tsx";
import { Icon } from "../components/Icon.tsx";
import { StatePanel } from "../components/StatePanel.tsx";
import { TrashTable } from "../components/TrashTable.tsx";
import ui from "../components/ui.module.css";
import { formatCount } from "../lib/format.ts";
import { messageOf, TRASH_RETENTION_DAYS, trashQuery, useEmptyTrash } from "../lib/queries.ts";
import trash from "./trash.module.css";

export const Route = createFileRoute("/trash")({
  loader: ({ context }) => context.queryClient.ensureInfiniteQueryData(trashQuery),
  component: TrashPage,
  pendingComponent: () => <ListPending title="Trash" />,
  errorComponent: TrashError,
});

const NOTE_MS = 5000;

function TrashPage() {
  const pages = useSuspenseInfiniteQuery(trashQuery);
  const items = pages.data.pages.flatMap((page) => page.items);
  const [restored, setRestored] = useState<string | null>(null);
  const [emptying, setEmptying] = useState(false);
  const empty = useEmptyTrash();

  useEffect(() => {
    document.title = "Trash · Vault";
  }, []);

  useEffect(() => {
    if (restored === null) return;
    const timer = setTimeout(() => setRestored(null), NOTE_MS);
    return () => clearTimeout(timer);
  }, [restored]);

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Trash</h1>
          <p className={trash.note}>
            Everything here is deleted for good {TRASH_RETENTION_DAYS} days after it landed.
          </p>
        </div>
        {items.length > 0 ? (
          <div className={styles.introActions}>
            <button
              type="button"
              className={ui.button}
              data-variant="frame"
              onClick={() => setEmptying(true)}
            >
              <Icon name="trash" />
              Empty trash
            </button>
          </div>
        ) : null}
      </div>

      <p className={trash.status} role="status">
        {restored ? (
          <span className={trash.restored}>
            <Icon name="check" />
            {restored} is back
          </span>
        ) : null}
      </p>

      {items.length === 0 ? (
        <div className={styles.folderEmpty}>
          <GooglyEyes />
          <p className={styles.folderEmptyTitle}>Trash is empty</p>
          <p className={styles.noMatchDetail}>
            Deleted files and folders wait here for {TRASH_RETENTION_DAYS} days.
          </p>
        </div>
      ) : (
        <TrashTable nodes={items} onRestored={(node) => setRestored(node.name)} />
      )}

      {pages.hasNextPage ? (
        <div className={styles.more}>
          <button
            type="button"
            className={ui.button}
            onClick={() => void pages.fetchNextPage()}
            disabled={pages.isFetchingNextPage}
          >
            {pages.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}

      <ConfirmDialog
        open={emptying}
        title={`Delete ${formatCount(items.length, "item", "items")} forever?`}
        confirmLabel={empty.isPending ? "Deleting…" : "Empty trash"}
        pending={empty.isPending}
        error={empty.error ? messageOf(empty.error) : null}
        onCancel={() => {
          setEmptying(false);
          empty.reset();
        }}
        onConfirm={() => empty.mutate(undefined, { onSuccess: () => setEmptying(false) })}
      >
        <p>Everything in the trash goes, with everything inside the folders.</p>
        <p>This deletes it for good.</p>
      </ConfirmDialog>
    </div>
  );
}

function TrashError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  return (
    <StatePanel icon="alert" title="Trash did not load" detail={messageOf(error)}>
      <button
        type="button"
        className={ui.button}
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
